import React, { PropsWithChildren } from 'react';
import { useRouter } from 'next/router';
import { Result } from 'antd';
import { Button } from '@bettercomp/ui-components';
import { navigateExternal } from '@bettercomp/ams-spa-auth';
import { GateView, useAuthContext } from '@bettercomp/ams-spa-auth-react';
import { getLoginUrl } from 'service/cognitoService';
import { isIdentityUnavailable } from 'components/AmsAuth/hostOutcomes';
import { navigateReplace } from 'components/AmsAuth/navigateReplace';

/**
 * Expired-session override for the packages' GateView: redirect through the
 * app's shared hosted-UI leg (getLoginUrl — PKCE'd, legacy-compatible) instead
 * of the package's beginLogin, because the login flow is decided per company
 * at the callback, not at redirect time. Module scope keeps the identity
 * referentially stable — the GateView contract.
 */
const redirectToSharedLogin = (path: string): void => {
  void getLoginUrl(path).then(navigateExternal);
};

/**
 * AMS-mode auth gate: the packages' outcome decision table on next/router, plus the app's own screen
 * for a failed api identity load, where sign-in itself worked.
 */
export function AmsGate({ children }: PropsWithChildren) {
  const router = useRouter();
  const { outcome, retry } = useAuthContext();

  // The package's copy blames the sign-in service; here AMS signed the person in and the api failed.
  if (isIdentityUnavailable(outcome)) {
    return (
      <Result
        data-testid="ams-workspace-unavailable"
        status="warning"
        title="We couldn't load your workspace"
        subTitle="Try again in a moment. If you have other workspaces, you can switch to one from the header."
        extra={
          <Button type="primary" onClick={retry}>
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <GateView currentPath={router.asPath} navigateReplace={navigateReplace} onSessionExpired={redirectToSharedLogin}>
      {children}
    </GateView>
  );
}
